import React from "react";
import LinkComponent from "../../app/components/link/LinkComponent";
import {AlertComponent} from "../../app/components/alert/AlertComponent";
import {ScreenComponent} from "../../app/components/screen/ScreenComponent";
import {GradientHeaderComponent} from "../../app/components/gradientheader/GradientHeaderComponent";
import {H2SectionComponent, H3SectionComponent} from "../../app/components/section/SectionComponent";
import {ThemeColor} from "../../home/components/homecard/HomeCardComponent";

export default function ITCodingInterviewScreen() {
    return (<ScreenComponent>
        <Header/>
        <General/>
        <HowToPrepare/>
    </ScreenComponent>)
}

function Header() {
    return (<GradientHeaderComponent title="Coding Interview"/>)
}

function General() {
    return (<H2SectionComponent header="General Tips & Tricks" removeTopPadding={true}>
        <GetOutPutIn/>
        <CompareYourself/>
        <WalkBeforeRun/>
        <WasteTime/>
        <Optimal/>
    </H2SectionComponent>)
}

function GetOutPutIn() {
    return (<H3SectionComponent removeTopPadding={true}
                                header="I said it before, I'll say it again, you will get out of this what you put into the process.">
        <p>You will not learn anything doing it once a week. You will not retain that information if you look at a
            topic once a month. You must continuously run drill over and over and over again.</p>
    </H3SectionComponent>)
}

function CompareYourself() {
    return (<H3SectionComponent header="Don't compare yourself to other LeetCoders.">
        <p>People cheat, people have no lives, people are weird. It doesn't matter how others are doing, what their
            ranks are, etc. I never did a single competition. It doesn't matter. FAANG isn't reaching out to you
            because you are the #237 LeetCoder in the world.</p>
    </H3SectionComponent>)
}

function WalkBeforeRun() {
    return (<H3SectionComponent header="You gotta learn how to walk before you can run.">
        <p>It is super discouraging when you start LeetCode for the first time, pick a random question, and then
            think it's completely mumbo jumbo with no idea how to solve it. In those moments, take a step back,
            figure out how to solve it - what is the pattern/algorithm/structure/etc - and try again later. You
            gotta learn it before you can ever try to solve them on your own.</p>
    </H3SectionComponent>)
}

function WasteTime() {
    return (<H3SectionComponent header="Don't spend more than an hour struggling to find an answer.">
        <p>There are so many resources out there that say you'll never learn by looking up a solution. And, to be
            fair, there is some merit to that if you just glance at the solution and that's it. But if you actually
            take the time to look it up, think about each line of code, what it's doing, read the explanation, walk
            through examples, etc. It will be worth it. After a while, I would reduce this time. You know you best,
            if you know you won't get a solution, just move on.</p>
    </H3SectionComponent>)
}

function Optimal() {
    return (<H3SectionComponent
        header="Getting an optimal solution working by the end is worth as little as 20% of the result.">
        <p>If I could only pick one mistake I see people making, it is this. Engineers truly love seeing things as
            black-and-white as possible. Pass/Fail. No middle ground. They think if you get an optimal solution,
            they've immediately passed. That is not true at all. Sure, it's important, but you <i>need</i> to be
            able to talk about your thought process eloquently. You need to be able to discuss time and space
            complexities and tradeoffs between different approaches. You need to be able to walk through examples
            and bugfix on the spot. <i>All</i> of this matters. So many people miss 1 problem and think it's all
            over, or on the contrary, they know they did perfect on finding the optimal solution for the coding
            problems and are surprised they didn't get an offer. This notion is wildly incorrect. The way you come
            across in an interview matters a lot.</p>
    </H3SectionComponent>)
}

function HowToPrepare() {
    return (<H2SectionComponent header="How Best To Prepare">
        <p>This particular section may be most helpful for people who struggle with LeetCode. For background, I took
            "Algorithms and Data Structures" in college, and failed the class. The only reason I didn't need to
            retake it is because a magical curve moved my grade from an F to a D, and that is technically enough to
            fill the required class. Safe to say, I knew almost nothing when I stared a few months back.</p>
        <AlertComponent color={ThemeColor.Secondary}><strong>NOTE:</strong> In the first two (2) steps, I talk about
            purchasing a LeetCode subscription to learn and practice problems. For a lot of people, this price tag may
            be out of reach for them. My hope is to use this site as a free method of learning about data structures and
            algorithms. While I will try my best to be a great place for learning these topics, this site will not be a
            great place for <i>practicing</i> problems. I will do my best to recommend free problems that correlate with
            the topics I'm covering, but I can personally say premium problems were helpful in me landing the role. For
            that reason, I still recommend paying for a LeetCode account if you can afford it. If you can't afford it,
            or just don't want to spend your hard earned cash on it, I hope this website can be useful in your journey
            in place of steps one (1) and two (2).</AlertComponent>
        <Prepare1/>
        <Prepare2/>
        <Prepare3/>
        <Prepare4/>
        <p>Overall, it took about a month and a half just to "learn" the things. Then I spent the next month and
            half just finding different lists to randomly go off of to make sure I stayed on top of it all. By the
            end of it, Mediums looked easy, and Hards were also looking Easy half the time. But I did <strong>PUT IN
                THE WORK</strong>. I spent <i>hours</i> on this daily. Including weekends. I didn't have much of a
            life outside of this for a while. If you already know the basic concepts, I doubt you'll need to go
            quite as hard, but it is possible if you want it. You just <i>really</i> have to want it.</p>
    </H2SectionComponent>)
}

function Prepare1() {
    return (<H3SectionComponent header="1. If you can, pay for LeetCode Premium" removeTopPadding={true}>
        <p>First thing I did, was pay for LeetCode. You can either pay $35 a month for LeetCode Premium, or $13.25 a
            month if you pay for a full year upfront. I chose a full year. I definitely didn't need that, but I
            don't regret it either. As I've now accepted a role, I'm making way more than that. It was worth the
            upfront investment. The reason that is critical for people who don't know what they are doing is because
            they have courses meant to help you learn the concepts, not just rehash them. And also, you can see the
            Editorials for the problems which will go through various methods of solving each problem as well as
            time and space complexity analysis. As someone who didn't even understand the concept of time/space
            complexity this was worth every single penny.</p>
    </H3SectionComponent>)
}

function Prepare2() {
    return (<H3SectionComponent header="2. Learn the basics">
        <p>Second thing I did, was the "Learn" courses found under the "Explore" tab. In this exact order, I did:
            Arrays 101, Array + String, Queue & Stack, Binary Tree, Recursion I, Recursion II, Graph, Binary Search,
            Binary Search Tree, Linked List, Sorting, Dynamic Programming, Heap, N-ary Tree, Trie, Comparator +
            Sorting.</p>
        <p>I highly recommend these courses. They do a great job at quickly explaining the topic, and then giving
            you problems related to it for you to do it on your own. Take for example, the Recursion II course. They
            introduce the topic of "Backtracking", and then have you immediately work on the N-Queens II problem to
            make sure you understand the concept. Then, just to be super-duper sure, they give you the template for
            such problems, before finally, throwing you into the deep end with 3 other Backtracking problems. Now,
            you can clearly see the name of the section, so you do get a clue that the problem is looking for a
            backtracking solution. Because of this, it's not the end-all-be-all. You won't get those clues in an
            interview, but it's great for learning patterns. I'm gonna drill into my head backtracking,
            backtracking, backtracking, and then what do you know? A few hours later, I know backtracking.</p>
    </H3SectionComponent>)
}

function Prepare3() {
    return (<H3SectionComponent header="3. Figure out current interview trends">
        <p>Third thing I did, was double down on some concepts I've seen people talking a lot about. At the time,
            "Merge Intervals" and "Permutations" were high up on the list. So, even though these weren't a specific
            data structure, or a typical algorithm, I spent some extra time on these, as well.</p>
    </H3SectionComponent>)
}

function Prepare4() {
    return (<H3SectionComponent header="4. Repetion">
        <p>Fourth thing I did, was drill drill drill. For this, I recommend looking at curated lists with a little
            bit of everything. Some I found particularly useful were:</p>
        <ul>
            <li><LinkComponent link="https://leetcode.com/list/oizxjoit">Blind 75</LinkComponent></li>
            <li><LinkComponent link="https://leetcode.com/list/rabvlt31">Grind 169</LinkComponent></li>
            <li><LinkComponent link="https://leetcode.com/list/rab78cw1">Grind 75</LinkComponent></li>
            <li><LinkComponent link="https://leetcode.com/list/rr2ss0g5">NeetCode 150</LinkComponent></li>
            <li><LinkComponent link="https://leetcode.com/list/o2qifkts">SQL 45</LinkComponent> (I didn't need this
                one for mobile, but in case it's helpful for others)
            </li>
        </ul>
    </H3SectionComponent>)
}
